// AppDataContext - Server-only version (no localStorage fallback)
// This version removes all localStorage fallbacks to ensure data consistency across multiple devices

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from './AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import inventoryService from '../services/inventoryService';
import stockService from '../services/stockService';
import dataIntegrityService from '../services/dataIntegrityService';

// API base URL - can be configured via environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AppDataContext = createContext();

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

export const AppDataProvider = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Application state
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingAppData, setIsLoadingAppData] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  
  // Shared data for all sidebar sections
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [returns, setReturns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [stockValuation, setStockValuation] = useState(null);
  const [departments, setDepartments] = useState([]);
  
  // Global app statistics
  const [globalStats, setGlobalStats] = useState({
    totalProducts: 0,
    totalStock: 0,
    totalSuppliers: 0,
    totalCustomers: 0,
    totalOrders: 0,
    totalSales: 0,
    totalPurchases: 0,
    totalWarehouses: 0,
    totalReturns: 0,
    totalInvoices: 0,
    lowStockItems: 0,
    pendingOrders: 0,
    revenue: 0,
    expenses: 0,
    profit: 0
  });

  // Track online status
  useEffect(() => {
    const handleOnline = () => {
      setNetworkError(false);
      toast.success('You are online. Syncing with server...', { id: 'online-status' });
      fetchAllData(); // Refresh data when coming back online
    };
    
    const handleOffline = () => {
      setNetworkError(true);
      toast.error('You are offline. Some features may be unavailable.', { id: 'offline-status' });
    };
    
    // Check initial status
    if (!navigator.onLine) {
      setNetworkError(true);
    }
    
    // Set up event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Clean up
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Function to update global stats
  const updateGlobalStats = (newStats) => {
    if (typeof newStats === 'function') {
      setGlobalStats(newStats);
    } else {
      setGlobalStats(prev => ({
        ...prev,
        ...newStats
      }));
    }
  };
  
  // Enhanced axios instance with error handling
  const apiClient = axios.create({
    baseURL: API_BASE_URL,
  });
  
  // Add response interceptor for consistent error handling
  apiClient.interceptors.response.use(
    response => response,
    error => {
      console.error('API Error:', error);
      if (!navigator.onLine) {
        toast.error('You are offline. Please check your connection.', {
          id: 'network-error',
          duration: 4000
        });
        setNetworkError(true);
      } else if (error.response) {
        if (error.response.status === 500) {
          toast.error('Server error. Please try again later.');
        }
      } else if (error.request) {
        toast.error('Cannot reach server. Please try again later.');
        setNetworkError(true);
      }
      return Promise.reject(error);
    }
  );

  // Function to fetch all data from server
  const fetchAllData = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingAppData) {
      return;
    }
    
    setIsLoadingAppData(true);
    
    // Reset network error state when trying to fetch
    setNetworkError(false);
    
    try {
      console.log('Fetching all data from server...');
      let fetchedData = {
        products: [],
        suppliers: [],
        customers: [],
        categories: [],
        purchases: [],
        sales: [],
        returns: []
      };
      
      // Load products from API
      try {
        const response = await apiClient.get('/products');
        const apiProducts = response.data || [];
        setProducts(apiProducts);
        console.log('Products loaded from API:', apiProducts.length);
        fetchedData.products = apiProducts;
        
        // Update global stats with the loaded products
        const productCount = apiProducts.length;
        const stockValue = apiProducts.reduce((sum, product) => {
          const price = parseFloat(product.price) || 0;
          const stock = parseInt(product.stock) || 0;
          return sum + (price * stock);
        }, 0);
        
        updateGlobalStats(prev => ({
          ...prev,
          totalProducts: productCount,
          totalStock: stockValue
        }));
        
        // Extract categories from products if needed
        const productsCategories = [...new Set(apiProducts.map(product => product.category))].filter(Boolean);
        if (productsCategories.length > 0) {
          setProductCategories(prev => prev.length ? prev : productsCategories);
          fetchedData.categories = productsCategories;
        }
      } catch (error) {
        console.error('Error loading products:', error);
        setNetworkError(true);
      }
      
      // Load suppliers from API
      try {
        const response = await apiClient.get('/suppliers');
        const apiSuppliers = response.data || [];
        setSuppliers(apiSuppliers);
        console.log('Suppliers loaded from API:', apiSuppliers.length);
        fetchedData.suppliers = apiSuppliers;
        
        updateGlobalStats(prev => ({ 
          ...prev,
          totalSuppliers: apiSuppliers.length 
        }));
      } catch (error) {
        console.error('Error loading suppliers:', error);
      }
      
      // Load customers from API
      try {
        const response = await apiClient.get('/customers');
        const apiCustomers = response.data || [];
        setCustomers(apiCustomers);
        console.log('Customers loaded from API:', apiCustomers.length);
        fetchedData.customers = apiCustomers;
        
        updateGlobalStats(prev => ({ 
          ...prev,
          totalCustomers: apiCustomers.length 
        }));
      } catch (error) {
        console.error('Error loading customers:', error);
      }
      
      // Load categories from API (if available)
      try {
        const response = await apiClient.get('/categories');
        const apiCategories = response.data || [];
        if (apiCategories.length > 0) {
          setProductCategories(apiCategories);
          console.log('Categories loaded from API:', apiCategories.length);
          fetchedData.categories = apiCategories;
        }
      } catch (error) {
        console.log('Categories API not available, using categories from products');
      }
      
      // Load purchases from API
      try {
        const response = await apiClient.get('/purchases');
        const apiPurchases = response.data || [];
        setPurchases(apiPurchases);
        console.log('Purchases loaded from API:', apiPurchases.length);
        fetchedData.purchases = apiPurchases;
        
        updateGlobalStats(prev => ({ 
          ...prev,
          totalPurchases: apiPurchases.length 
        }));
      } catch (error) {
        console.error('Error loading purchases:', error);
      }
      
      // Load sales from API
      try {
        const response = await apiClient.get('/sales');
        const apiSales = response.data || [];
        setSales(apiSales);
        console.log('Sales loaded from API:', apiSales.length);
        fetchedData.sales = apiSales;
        
        updateGlobalStats(prev => ({ 
          ...prev,
          totalSales: apiSales.length 
        }));
      } catch (error) {
        console.error('Error loading sales:', error);
      }
      
      // Load returns from API
      try {
        const response = await apiClient.get('/returns');
        const apiReturns = response.data || [];
        setReturns(apiReturns);
        console.log('Returns loaded from API:', apiReturns.length);
        fetchedData.returns = apiReturns;
        
        updateGlobalStats(prev => ({ 
          ...prev,
          totalReturns: apiReturns.length 
        }));
      } catch (error) {
        console.error('Error loading returns:', error);
      }
      
      // Load stock movements from API
      try {
        const response = await apiClient.get('/stock/movements');
        const apiStockMovements = response.data || [];
        setStockItems(apiStockMovements);
        console.log('Stock movements loaded from API:', apiStockMovements.length);
        fetchedData.stockMovements = apiStockMovements;
      } catch (error) {
        console.error('Error loading stock movements:', error);
      }
      
      // Load warehouses from API
      try {
        const response = await apiClient.get('/warehouses');
        const apiWarehouses = response.data || [];
        setWarehouses(apiWarehouses);
        console.log('Warehouses loaded from API:', apiWarehouses.length);
        fetchedData.warehouses = apiWarehouses;
        
        updateGlobalStats(prev => ({ 
          ...prev,
          totalWarehouses: apiWarehouses.length 
        }));
      } catch (error) {
        console.error('Error loading warehouses:', error);
      }
      
      // Load departments from API
      try {
        const response = await apiClient.get('/departments');
        const apiDepartments = response.data || [];
        setDepartments(apiDepartments);
        console.log('Departments loaded from API:', apiDepartments.length);
        fetchedData.departments = apiDepartments;
      } catch (error) {
        console.error('Error loading departments:', error);
      }
      
      // Load notifications from API
      try {
        const response = await apiClient.get('/notifications');
        const apiNotifications = response.data || [];
        // Convert string timestamps back to Date objects
        const processedNotifications = apiNotifications.map(notif => ({
          ...notif,
          timestamp: notif.timestamp ? new Date(notif.timestamp) : new Date()
        }));
        setNotifications(processedNotifications);
        setUnreadCount(processedNotifications.filter(n => !n.read).length);
        console.log('Notifications loaded from API:', processedNotifications.length);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
      
      // Load stock alerts
      try {
        const alerts = await stockService.getStockAlerts();
        setStockAlerts(alerts);
        console.log('Stock alerts loaded:', alerts.length);
        
        updateGlobalStats(prev => ({
          ...prev,
          lowStockItems: alerts.length
        }));
      } catch (error) {
        console.error('Error loading stock alerts:', error);
      }
      
      // Load stock valuation
      try {
        const valuation = await stockService.getStockValuation();
        setStockValuation(valuation);
        console.log('Stock valuation loaded');
        
        updateGlobalStats(prev => ({
          ...prev,
          totalStock: valuation.totalValue || prev.totalStock
        }));
      } catch (error) {
        console.error('Error loading stock valuation:', error);
      }
      
      return fetchedData;
    } catch (error) {
      console.error('Error fetching data:', error);
      setNetworkError(true);
      return {};
    } finally {
      setIsLoadingAppData(false);
    }
  }, []);
  
  // Load all data on component mount
  useEffect(() => {
    fetchAllData();
    
    // Add a global refresh function to window for direct access from any component
    window.refreshAppData = async () => {
      console.log('Global app data refresh triggered');
      return await fetchAllData();
    };
    
    // Clean up
    return () => {
      delete window.refreshAppData;
    };
  }, [fetchAllData]);
  
  // Add notification function
  const addNotification = async (notification) => {
    try {
      // Create notification object
      const newNotification = {
        id: Date.now(),
        timestamp: new Date(),
        read: false,
        ...notification
      };
      
      // Save to server
      const response = await apiClient.post('/notifications', newNotification);
      const savedNotification = response.data;
      
      // Update state
      setNotifications(prev => [savedNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Show toast notification
      toast(
        <div>
          <strong>{savedNotification.title}</strong>
          <p className="text-sm">{savedNotification.message}</p>
        </div>,
        {
          icon: savedNotification.type === 'warning' ? '⚠️' : 
                 savedNotification.type === 'error' ? '❌' : 
                 savedNotification.type === 'success' ? '✅' : 'ℹ️',
          duration: 4000
        }
      );
      
      return savedNotification.id;
    } catch (error) {
      console.error('Error adding notification:', error);
      
      // Even if server save fails, show the notification to the user
      const localNotification = {
        id: Date.now(),
        timestamp: new Date(),
        read: false,
        ...notification
      };
      
      setNotifications(prev => [localNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      toast(
        <div>
          <strong>{notification.title}</strong>
          <p className="text-sm">{notification.message}</p>
        </div>,
        {
          icon: notification.type === 'warning' ? '⚠️' : 
                 notification.type === 'error' ? '❌' : 
                 notification.type === 'success' ? '✅' : 'ℹ️',
          duration: 4000
        }
      );
      
      return localNotification.id;
    }
  };
  
  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      // Update on server
      await apiClient.put(`/notifications/${notificationId}`, { read: true });
      
      // Update local state
      setNotifications(prev => {
        const updatedNotifications = prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        );
        return updatedNotifications;
      });
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      
      // Still update UI even if server fails
      setNotifications(prev => {
        const updatedNotifications = prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        );
        return updatedNotifications;
      });
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };
  
  // Add a product
  const addProduct = async (product) => {
    try {
      // Create a copy of the product for data manipulation
      const productToSave = { ...product };
      
      // Handle image upload (in a real app, you would upload to server)
      if (productToSave.image && (productToSave.image instanceof File || productToSave.image instanceof Blob)) {
        productToSave.imageUrl = URL.createObjectURL(productToSave.image);
        productToSave.image = productToSave.imageUrl;
      }
      
      // Generate an ID if not provided
      if (!productToSave.id) {
        const prefix = productToSave.name ? productToSave.name.slice(0, 3).toUpperCase() : 'PRD';
        const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        productToSave.id = `${prefix}-${randomNum}`;
      }
      
      // Ensure costPrice is stored as cost for compatibility
      if (productToSave.costPrice && !productToSave.cost) {
        productToSave.cost = productToSave.costPrice;
      }
      
      // Make sure numeric fields are properly parsed
      if (typeof productToSave.price === 'string') {
        productToSave.price = parseFloat(productToSave.price) || 0;
      }
      if (typeof productToSave.cost === 'string') {
        productToSave.cost = parseFloat(productToSave.cost) || 0;
      }
      if (typeof productToSave.stock === 'string') {
        productToSave.stock = parseInt(productToSave.stock) || 0;
      }
      
      // Set timestamps
      productToSave.createdAt = new Date().toISOString();
      productToSave.updatedAt = new Date().toISOString();
      
      // Save to server
      const response = await apiClient.post('/products', productToSave);
      const savedProduct = response.data;
      
      console.log('Product saved to server successfully:', savedProduct.name);
      
      // Update state
      setProducts(prev => [...prev, savedProduct]);
      
      // Update product count in global stats
      updateGlobalStats(prev => ({
        ...prev,
        totalProducts: prev.totalProducts + 1
      }));
      
      // Dispatch event for dashboard and other components to refresh
      const productAddedEvent = new CustomEvent('product-added', {
        detail: { productId: savedProduct.id }
      });
      window.dispatchEvent(productAddedEvent);
      
      // Also dispatch a general refresh event
      const refreshEvent = new CustomEvent('refresh-data');
      window.dispatchEvent(refreshEvent);
      
      return savedProduct;
    } catch (error) {
      console.error('Error adding product:', error);
      throw error;
    }
  };
  
  // Update a product
  const updateProduct = async (product) => {
    try {
      const productToUpdate = { ...product };
      
      // Handle image upload
      if (productToUpdate.image && (productToUpdate.image instanceof File || productToUpdate.image instanceof Blob)) {
        productToUpdate.imageUrl = URL.createObjectURL(productToUpdate.image);
        productToUpdate.image = productToUpdate.imageUrl;
      }
      
      // Set updated timestamp
      productToUpdate.updatedAt = new Date().toISOString();
      
      // Update on server
      const response = await apiClient.put(`/products/${productToUpdate.id}`, productToUpdate);
      const savedProduct = response.data;
      
      console.log('Product updated on server successfully:', savedProduct.name);
      
      // Update state
      setProducts(prev => prev.map(p => p.id === savedProduct.id ? savedProduct : p));
      
      // Dispatch event for dashboard and other components to refresh
      const productUpdatedEvent = new CustomEvent('product-updated', {
        detail: { productId: savedProduct.id }
      });
      window.dispatchEvent(productUpdatedEvent);
      
      // Also dispatch a general refresh event
      const refreshEvent = new CustomEvent('refresh-data');
      window.dispatchEvent(refreshEvent);
      
      return savedProduct;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };
  
  // Delete a product
  const deleteProduct = async (id) => {
    try {
      // Delete from server
      await apiClient.delete(`/products/${id}`);
      
      // Update state
      setProducts(prev => prev.filter(p => p.id !== id));
      
      // Update global stats
      updateGlobalStats(prev => ({
        ...prev,
        totalProducts: prev.totalProducts - 1
      }));
      
      // Dispatch event for dashboard and other components to refresh
      const productDeletedEvent = new CustomEvent('product-deleted', {
        detail: { productId: id }
      });
      window.dispatchEvent(productDeletedEvent);
      
      // Also dispatch a general refresh event
      const refreshEvent = new CustomEvent('refresh-data');
      window.dispatchEvent(refreshEvent);
      
      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      return false;
    }
  };
  
  // Add stock movement
  const addStockMovement = async (movement) => {
    try {
      // Check for flags that indicate this movement comes from a form
      const isFromForm = movement.fromEntryForm || movement.fromExitForm;
      console.log(`Adding stock movement${isFromForm ? ' from form' : ''}:`, movement);
      
      // Add to server
      const response = await apiClient.post('/stock/movements', movement);
      const newMovement = response.data;
      
      // Update state
      setStockItems(prev => [newMovement, ...prev]);
      
      // Handle items array for multi-product movements
      if (movement.items && Array.isArray(movement.items) && movement.items.length > 0) {
        console.log('Processing multi-product movement with items:', movement.items.length);
        
        // Skip updateProductStock if this is from a form that already calls it
        if (!movement.fromEntryForm && !movement.fromExitForm) {
          for (const item of movement.items) {
            if (!item.id) continue;
            
            try {
              await updateProductStock(item.id, {
                type: movement.type,
                quantity: parseInt(item.quantity) || 0,
                warehouse: movement.warehouse || movement.location,
                reference: movement.reference,
                notes: movement.notes,
                skipMovementRecord: true
              });
            } catch (itemError) {
              console.error(`Error updating stock for item ${item.id}:`, itemError);
            }
          }
        }
      }
      // Handle single product movements
      else if (movement.productId && movement.quantity) {
        if (!movement.fromEntryForm && !movement.fromExitForm) {
          await updateProductStock(movement.productId, {
            type: movement.type,
            quantity: parseInt(movement.quantity) || 0,
            warehouse: movement.warehouse || movement.location,
            reference: movement.reference,
            notes: movement.notes,
            skipMovementRecord: true
          });
        }
      }
      
      // Refresh alerts and valuation
      try {
        const alerts = await stockService.getStockAlerts();
        setStockAlerts(alerts);
        const valuation = await stockService.getStockValuation();
        setStockValuation(valuation);
      } catch (refreshError) {
        console.error('Error refreshing stock data:', refreshError);
      }
      
      return newMovement;
    } catch (error) {
      console.error('Error adding stock movement:', error);
      toast.error('Erreur lors de l\'ajout du mouvement de stock');
      throw error;
    }
  };
  
  // Delete stock movement
  const deleteStockMovement = async (id) => {
    try {
      // Delete from server
      await apiClient.delete(`/stock/movements/${id}`);
      
      // Update state
      setStockItems(prev => prev.filter(item => item.id !== id));
      
      // Refresh alerts and valuation
      try {
        const alerts = await stockService.getStockAlerts();
        setStockAlerts(alerts);
        const valuation = await stockService.getStockValuation();
        setStockValuation(valuation);
      } catch (refreshError) {
        console.error('Error refreshing stock data after deletion:', refreshError);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting stock movement:', error);
      toast.error('Erreur lors de la suppression du mouvement de stock');
      return false;
    }
  };
  
  // Update product stock
  const updateProductStock = async (productId, movementData) => {
    try {
      console.log('Updating product stock:', productId, movementData);
      
      // Update stock on server
      const response = await apiClient.post(`/stock/products/${productId}/stock`, movementData);
      const updatedProduct = response.data;
      
      // Update state
      setProducts(prev => {
        const updatedProducts = [...prev];
        const productIndex = updatedProducts.findIndex(p => p.id === productId);
        
        if (productIndex !== -1) {
          updatedProducts[productIndex] = updatedProduct;
        }
        
        return updatedProducts;
      });
      
      // Update stockItems if a new movement was created
      if (!movementData.skipMovementRecord && !movementData.isTransfer) {
        try {
          const response = await apiClient.get('/stock/movements');
          setStockItems(response.data || []);
        } catch (error) {
          console.error('Error refreshing stock movements:', error);
        }
      }
      
      // Dispatch event
      const stockUpdateEvent = new CustomEvent('product-stock-updated', {
        detail: { 
          productId,
          warehouse: movementData.warehouse,
          type: movementData.type,
          quantity: movementData.quantity
        }
      });
      
      window.dispatchEvent(stockUpdateEvent);
      
      return updatedProduct;
    } catch (error) {
      console.error('Error updating product stock:', error);
      toast.error(`Erreur lors de la mise à jour du stock: ${error.message}`);
      throw error;
    }
  };

  // Export all functionality through context
  const value = {
    // State
    selectedMember,
    setSelectedMember,
    selectedEmployee,
    setSelectedEmployee,
    selectedProduct,
    setSelectedProduct,
    notifications,
    unreadCount,
    globalStats,
    networkError,
    
    // Shared data between sections
    products,
    suppliers,
    customers,
    orders,
    sales,
    purchases,
    warehouses,
    returns,
    invoices,
    stockItems,
    productCategories,
    inventoryItems,
    stockAlerts,
    stockValuation,
    departments,
    
    // Loading and update functions
    fetchAllData,
    updateGlobalStats,
    isLoadingAppData,
    
    // CRUD functions for products
    addProduct,
    updateProduct,
    deleteProduct,
    getProducts: () => products,
    getProductById: (id) => products.find(product => product.id === id),
    getLowStockProducts: () => products.filter(product => product.stock <= (product.minStockLevel || 5)),
    
    // Stock movement functions
    addStockMovement,
    deleteStockMovement,
    updateProductStock,
    
    // Notification functions
    addNotification,
    markAsRead,
    
    // API client for direct access if needed
    apiClient,

    // Similar CRUD functions would be implemented for other entities
    // such as suppliers, customers, orders, etc.
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};